import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, LogOut, Menu, X } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { Logout } from '@/components/shared/Logout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/hooks/useAuth';
import useMenuItems from '@/lib/hooks/useMenuItems';
import { cn } from '@/lib/utils/cn';
import { useSettingsStore } from '@/store';

export const MobileMenu = () => {
  const { isAuthenticated } = useAuth();

  const { mobileMenuItems, isLoading } = useMenuItems();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { settings } = useSettingsStore();

  const themedUi = settings?.ui?.theming?.themedUi;

  // Prevent body scroll when menu is open
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
    }

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden relative group p-2 rounded-lg hover:bg-accent transition-colors duration-200"
        aria-label="Toggle mobile menu"
      >
        <Menu
          className={cn(
            'h-6 w-6 transition-colors duration-200',
            themedUi
              ? 'text-primary-foreground group-hover:text-foreground'
              : 'text-muted-foreground group-hover:text-foreground'
          )}
        />
        <span className="absolute bottom-1 left-1/2 w-0 h-0.5 bg-primary rounded-full transform -translate-x-1/2 transition-all duration-300 group-hover:w-1/2" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm h-[100dvh]"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              ref={menuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0  z-50 h-[100dvh] w-[280px] bg-background border-l shadow-lg overflow-hidden"
              style={{ height: '100dvh' }}
            >
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b">
                  <h2 className="text-xl font-semibold">Navigation</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-full hover:bg-accent transition-colors duration-200"
                    aria-label="Close menu"
                  >
                    <X className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>
                <div className="p-4 flex flex-col gap-4 justify-between overflow-y-auto flex-1 overflow-hidden">
                  <nav>
                    <ul className="space-y-2">
                      {isLoading ? (
                        <>
                          <Skeleton className="h-6 w-full" />
                          <Skeleton className="h-6 w-full" />
                          <Skeleton className="h-6 w-full" />
                          <Skeleton className="h-6 w-full" />
                          <Skeleton className="h-6 w-full" />
                        </>
                      ) : (
                        mobileMenuItems.map((item, index) => (
                          <motion.li
                            key={item.path}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <NavLink
                              to={item.path}
                              onClick={() => setIsOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-foreground hover:bg-accent'
                                }`
                              }
                            >
                              <span>{item.title}</span>
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </NavLink>
                          </motion.li>
                        ))
                      )}
                    </ul>
                  </nav>
                  {isAuthenticated && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Logout>
                        <Button
                          variant="destructive"
                          className="mt-auto w-full"
                        >
                          <LogOut size={16} className="mr-2" /> Log out
                        </Button>
                      </Logout>
                    </motion.div>
                  )}
                </div>

                <motion.footer
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="w-full p-4 border-t text-xs text-muted-foreground overflow-hidden"
                >
                  © {new Date().getFullYear()} {settings?.ui?.organizationName}{' '}
                </motion.footer>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
